import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import type { Updater } from '@tanstack/react-table'
import React, { useCallback, useMemo, useState } from 'react'

import { buildColumnsDef } from './helpers'
import type { CoreTableProps, SortingState } from './types'
import {
  Table,
  TableBody,
  TableContextProvider,
  TableHeader,
  TableHeaderRow,
  TableRow
} from '../components'
import type { TableRowProps } from '../components'
import { useIsMobileView } from '../hooks'

export function CoreTable<T>(args: CoreTableProps<T>) {
  const { resizableColumn, data, className, onRow, sortingProps } = args
  //TODO(awad): implement multi column sorting :   //const { sorting = undefined, sortingState = [], enableMultiSort = false } = sortingProps || {};
  const { sorting = undefined, sortingState = [] } = sortingProps || {}

  const isClientSideSorting = sorting === true
  const [internalSort, setInternalSort] = useState<SortingState>(
    sortingState ?? []
  )

  const effectiveSort = isClientSideSorting ? internalSort : sortingState

  const sortingHandler = useCallback(
    (updater: Updater<SortingState>) => {
      if (isClientSideSorting) {
        // Update internal sort state for client-side sorting
        setInternalSort(prevSort => {
          return typeof updater === 'function' ? updater(prevSort) : updater
        })
      } else if (typeof sorting === 'function') {
        // Call external sorting function for manual sorting
        sorting(typeof updater === 'function' ? updater(sortingState) : updater)
      }
    },
    [isClientSideSorting, sorting, sortingState]
  )

  const isMobile = useIsMobileView()
  const { columns, leftStickColumns } = useMemo(
    () => buildColumnsDef({ isMobile, ...args }),
    [args, isMobile]
  )
  const memoManualSorting = useMemo(
    () => (sorting && typeof sorting === 'function' ? true : undefined),
    [sorting]
  )

  const memoData = useMemo(() => data, [data])
  const memoColumns = useMemo(() => columns, [columns])

  const table = useReactTable({
    state: {
      columnPinning: {
        left: leftStickColumns
      },
      sorting: effectiveSort
    },
    data: memoData,
    columns: memoColumns,
    //onColumnPinningChange: setColumnPinning,
    enableColumnResizing: resizableColumn,
    enablePinning: leftStickColumns?.length > 0,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    debugHeaders: false,
    //enableRowSelection: true,
    debugColumns: false,
    // enableMultiSort: enableMultiSort, TODO(awad): implement multi column sorting
    manualSorting: memoManualSorting,
    onSortingChange: sortingHandler,
    getSortedRowModel: isClientSideSorting ? getSortedRowModel() : undefined // if sorting === true we'll do a client side sort, else its up to the consumer to set up sorting fn
  })

  //const footerGroups = table.getFooterGroups()
  const cellProps: TableRowProps<T>['cellProps'] = useMemo(
    () => ({
      isMobile: isMobile
    }),
    [isMobile]
  )

  return (
    <TableContextProvider {...args}>
      <Table className={`${className || ''}`}>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableHeaderRow key={headerGroup.id} headerGroup={headerGroup} />
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row, index) => (
            <TableRow
              key={row.id}
              index={index}
              row={row}
              onRow={onRow}
              className="[&:nth-child(even)_td]:bg-neutral-lighter"
              cellProps={cellProps}
            />
          ))}
        </TableBody>
        {/* <TableFooter>
            <TableFooterRow
              className="text-neutral-dark"
              footerGroup={footerGroups[0]}
            />
          </TableFooter> */}
      </Table>
    </TableContextProvider>
  )
}
